import { IUser } from 'interface/user';
import { useRef, useState, ChangeEvent } from 'react';
import { makeStyles, Typography, Hidden, Box, useMediaQuery } from '@material-ui/core';
import IconSearch from '@eduzz/houston-icons/SearchOutline';
import IconChevronDown from '@eduzz/houston-icons/ChevronDown';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useGlobalSearch } from 'hooks/globalSearch';
interface IProps {
  user: IUser;
  onChangeViewMode: (viewMode: string) => void;
}

const useStyles = makeStyles((theme) => ({
  container: {
    borderBottom: `1px solid ${theme.palette.dark[600]}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(4, 8),
    '& > div': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    '@media(max-width: 1024px)': {
      padding: theme.spacing(2, 4),
      '& h2,p': {
        fontSize: 24,
      },
    },
  },
  whiteText: {
    display: 'flex',
    color: theme.palette.common.white,
  },
  name: {
    color: theme.palette.common.white,
    margin: theme.spacing(0, 2),
    marginRight: 0,
    maxWidth: 300,
    fontSize: 32,
    lineHeight: 1.2,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  selectRoot: {
    marginRight: theme.spacing(-4),
    '& > div': {
      '&:after, &:before': {
        content: 'none !important',
      },
    },
  },
  selectMenu: {
    border: 'none !important',
    paddingRight: 0,
    paddingLeft: theme.spacing(6),
    fontSize: 14,
  },
  selectIcon: {
    top: 'calc(50% - 8px)',
    left: 0,
    color: theme.palette.common.white,
  },
  dropdownMenu: {
    background: theme.palette.dark[600],
    overflow: 'visible',
    '&:after': {
      content: "''",
      position: 'absolute',
      backgroundColor: 'transparent',
      borderLeft: '10px solid transparent',
      borderRight: '10px solid transparent',
      borderBottom: `10px solid ${theme.palette.dark[600]}`,
      top: -10,
      right: 20,
    },
    '& li': {
      color: theme.palette.common.white,
    },
  },
  inputContainer: {
    marginRight: theme.spacing(4),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    background: theme.palette.safevideo.dark,
    borderRadius: theme.shape.borderRadius,
    height: 35,
    width: 94,
    transition: '0.3s ease',
    '&:hover': {
      width: 320,
    },
    '& svg': {
      fill: theme.palette.common.white,
      marginBottom: theme.spacing(-1),
    },
    '& input': {
      background: theme.palette.safevideo.dark,
      color: theme.palette.common.white,
      borderWidth: '0px',
      paddingLeft: '0px !important',
      margin: '0px !important',
      height: '35px !important',
      shadowBox: 'none',
    },
  },
  input: {
    borderWidth: '0px !important',
    paddingLeft: '0px !important',
    margin: '0px !important',
    height: '35px !important',
    '&:focus': {
      boxShadow: 'none !important',
    },
  },
}));

const Filters = (props: IProps) => {
  const classes = useStyles();
  const [hasText, setHasText] = useState(false);
  const [viewMode, setViewMode] = useState('none');
  const disableHeaderSearch = useMediaQuery('@media(min-width: 960px)');
  const { onSearchChange } = useGlobalSearch({
    disabled: disableHeaderSearch,
    placeholder: 'Buscar por curso, autor(a) ou produtor(a)',
  });
  const { current: viewModeOptions } = useRef([
    { key: 'shelves', name: 'Lista de categorias' },
    { key: 'grid', name: 'Todos' },
  ]);

  const getFirstName = () => {
    if (!props.user.name) return '';
    if (props.user.name.includes(' ')) {
      return props.user.name.split(' ')[0];
    }
    return props.user.name;
  };

  const onChangeViewMode = (event: ChangeEvent<{ value: string }>) => {
    setViewMode(event.target.value);
    props.onChangeViewMode(event.target.value);
  };

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setHasText((event.target.value ?? '').length > 0);
    onSearchChange(event);
  };

  const NameElement = (
    <Typography className={classes.name} id='courses-filter-userName'>
      {getFirstName()}
    </Typography>
  );

  return (
    <Hidden smDown>
      <div id='showcase-filter-bar' className={classes.container}>
        <Typography className={classes.whiteText} variant='h2'>
          Olá, {NameElement}. Que bom te ver por aqui!
        </Typography>

        <div id='showcase-search-input'>
          <div style={{ width: hasText ? 320 : undefined }} className={`${classes.inputContainer} old-input`}>
            <Box margin={2}>
              <IconSearch />
            </Box>
            <input
              className={classes.input}
              onChange={onSearch}
              placeholder='Buscar por curso, autor(a) ou produtor(a)'
            />
          </div>
          <div id='showcase-select-view-mode' className={classes.selectRoot}>
            <Select
              classes={{ select: classes.whiteText, selectMenu: classes.selectMenu, icon: classes.selectIcon }}
              value={viewMode}
              MenuProps={{
                classes: { paper: classes.dropdownMenu },
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
              }}
              onChange={onChangeViewMode}
              IconComponent={(props) => <IconChevronDown {...props} size={14} />}
            >
              <MenuItem value='none' disabled>
                Exibir por
              </MenuItem>
              {viewModeOptions.map((option, key) => (
                <MenuItem key={key} value={option.key}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>
    </Hidden>
  );
};

export default Filters;
